import { z } from "zod";
import { LocalIdSchema, SpotifyIdSchema } from "./common.ts";

/** Playlist nodes in the explorer — Spotify mirrors or local workspace lists. */
export const ExplorePlaylistKindSchema = z.enum(["playlist", "local"]);
export type ExplorePlaylistKind = z.infer<typeof ExplorePlaylistKindSchema>;

export const ExploreArtistNodeSchema = z.object({
  kind: z.literal("artist"),
  id: SpotifyIdSchema,
  name: z.string().nullable(),
  image_url: z.string().nullable(),
  /** Tracks credited to this artist inside the request’s playlist (or 0). */
  track_count: z.number().int().nonnegative(),
});
export type ExploreArtistNode = z.infer<typeof ExploreArtistNodeSchema>;

export const ExplorePlaylistNodeSchema = z.object({
  kind: ExplorePlaylistKindSchema,
  id: z.string().min(1),
  name: z.string().nullable(),
  image_url: z.string().nullable(),
  /** Distinct tracks in the playlist (or artist∩playlist when from an artist expand). */
  track_count: z.number().int().nonnegative(),
});
export type ExplorePlaylistNode = z.infer<typeof ExplorePlaylistNodeSchema>;

/** artist → playlist: “this artist has tracks in this playlist”. */
export const ExploreEdgeSchema = z.object({
  artist_id: SpotifyIdSchema,
  playlist_kind: ExplorePlaylistKindSchema,
  playlist_id: z.string().min(1),
  track_count: z.number().int().positive(),
});
export type ExploreEdge = z.infer<typeof ExploreEdgeSchema>;

export const ExploreArtistsResponseSchema = z.object({
  playlist: ExplorePlaylistNodeSchema,
  artists: z.array(ExploreArtistNodeSchema),
  total: z.number().int().nonnegative(),
  offset: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  edges: z.array(ExploreEdgeSchema),
});
export type ExploreArtistsResponse = z.infer<typeof ExploreArtistsResponseSchema>;

export const ExplorePlaylistsResponseSchema = z.object({
  artist: ExploreArtistNodeSchema,
  playlists: z.array(ExplorePlaylistNodeSchema),
  total: z.number().int().nonnegative(),
  offset: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  edges: z.array(ExploreEdgeSchema),
});
export type ExplorePlaylistsResponse = z.infer<typeof ExplorePlaylistsResponseSchema>;

export const ExploreSeedQuerySchema = z.object({
  kind: ExplorePlaylistKindSchema,
  id: z.string().min(1),
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

export const ExploreArtistQuerySchema = z.object({
  id: SpotifyIdSchema,
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

/** Canvas / persist key for an explorer node. */
export function exploreNodeKey(
  node:
    | { kind: "artist"; id: string }
    | { kind: ExplorePlaylistKind; id: string },
): string {
  if (node.kind === "artist") return `a:${node.id}`;
  if (node.kind === "playlist") return `p:${node.id}`;
  return `l:${node.id}`;
}

export function parseExploreNodeKey(
  key: string,
): { kind: "artist"; id: string } | { kind: ExplorePlaylistKind; id: string } | null {
  const colon = key.indexOf(":");
  if (colon <= 0) return null;
  const prefix = key.slice(0, colon);
  const id = key.slice(colon + 1);
  if (!id) return null;
  if (prefix === "a") {
    const ok = SpotifyIdSchema.safeParse(id);
    return ok.success ? { kind: "artist", id } : null;
  }
  if (prefix === "p") {
    const ok = SpotifyIdSchema.safeParse(id);
    return ok.success ? { kind: "playlist", id } : null;
  }
  if (prefix === "l") {
    const ok = LocalIdSchema.safeParse(id);
    return ok.success ? { kind: "local", id } : null;
  }
  return null;
}

export function exploreSeedKey(kind: ExplorePlaylistKind, id: string): string {
  return exploreNodeKey({ kind, id });
}

export function exploreEdgeKey(edge: ExploreEdge): string {
  return `${edge.artist_id}|${edge.playlist_kind}:${edge.playlist_id}`;
}
